import { Component, ElementRef, HostListener, NgZone, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/api/global.service';
import { AlertController, ModalController } from '@ionic/angular';
import { AddressBookComponent } from '../../address-book/address-book.component';
import { TemplateBookComponent } from '../../projects/template-book/template-book.component';
import { DetailsComponent } from '../../team-members/details/details.component';
import { AuthService } from 'src/app/auth.service';
import { ApiService } from 'src/app/api/api.service';
import { DetailsPage as TaskDetailsPage } from 'src/app/tasks/details/details.page';
import { DetailsPage as EmailDetailsPage } from 'src/app/secure-email/details/details.page';
import { DetailsPage as ProjectDetailsPage } from 'src/app/projects/details/details.page';
import { DetailsPage as StepDetailsPage } from 'src/app/steps/details/details.page';
import { DetailsPage as ClientDetailsPage } from 'src/app/clients/details/details.page';
import { DetailsPage as CompanyDetailsPage } from 'src/app/companies/details/details.page';
import { DetailsPage as MissingInfoDetailsPage } from 'src/app/missing-info/details/details.page';
import { CreateComponent as AppointmentCreateComponent } from '../../team-members/calendar/appointments/create/create.component';
import { NotificationTypes } from './NotificationTypes';
import { TodosService } from 'src/app/api/todos.service';
import { TodoEditComponent } from '../../todos/edit/todo-edit.component';
import { Subscription } from 'rxjs';
import * as dayjs from 'dayjs';

@Component({
  selector: 'app-top-navbar',
  templateUrl: './top-navbar.component.html',
  styleUrls: ['./top-navbar.component.scss'],
})

export class TopNavbarComponent implements OnInit, AfterViewInit, OnDestroy {

  notificationTypeEnum = NotificationTypes;
  user: any = null;
  notifications: any[] = [];
  unreadCount = 0;
  showNotifications = false;
  loadingNotifications = false;
  selectedNotificationType: NotificationTypes | null = null;
  forceMobile = false;
  // Missing info items the client has fulfilled their side of, but a team member hasn't reviewed
  // and marked complete yet - shown as a red dot on the Missing Info nav link.
  missingInfoAwaitingReviewCount = 0;
  // Open To Dos assigned to the current team member - shown as a red dot on the To Do nav link.
  openTodoCount = 0;
  private todosChangedSub: Subscription;
  private refreshInterval: any;
  private resizeObserver: ResizeObserver | null = null;
  private navLinksNaturalWidth = 0;

  notificationTypeConfig = [
    { type: NotificationTypes.task,         label: 'Tasks',        icon: 'fa-list-check' },
    { type: NotificationTypes.email,        label: 'Emails',       icon: 'fa-envelope' },
    { type: NotificationTypes.project,      label: 'Projects',     icon: 'fa-diagram-project' },
    { type: NotificationTypes.missing_info, label: 'Missing Info', icon: 'fa-circle-question' },
    { type: NotificationTypes.appointment,  label: 'Appointments', icon: 'fa-calendar-check' },
    { type: NotificationTypes.client,       label: 'Clients',      icon: 'fa-user' },
    { type: NotificationTypes.company,      label: 'Companies',    icon: 'fa-building' },
    { type: NotificationTypes.step,         label: 'Actions',      icon: 'fa-shoe-prints' },
    { type: NotificationTypes.files,        label: 'Files',        icon: 'fa-file-arrow-up' },
    { type: NotificationTypes.todo,         label: 'To Do',        icon: 'fa-square-check' },
  ];

  constructor(
    public globalService: GlobalService,
    private alertController: AlertController,
    private modalController: ModalController,
    private authService: AuthService,
    private apiService: ApiService,
    private elementRef: ElementRef,
    private ngZone: NgZone,
    private router: Router,
    private todosService: TodosService
  ) { }

  ngOnInit() {
    this.user = this.globalService.GetCurrentUser();
    this.loadNotifications();
    this.loadMissingInfoAwaitingReviewCount();
    this.loadOpenTodoCount();
    this.todosChangedSub = this.todosService.changed.subscribe(() => this.loadOpenTodoCount());
    this.startRefreshTimer();
    this.CheckReopenYourAccount();
  }

  // Connect Outlook/Connect Gmail (details.component.ts) redirect out to Microsoft/Google and back -
  // a full page load, so the "Your Account" modal they were opened from is gone by the time the
  // browser lands back here. Those methods tack a your_account=open marker onto the OAuth return_url
  // for exactly this reason - reopen the modal here, then strip the marker back out of the address bar.
  private async CheckReopenYourAccount() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('your_account') !== 'open') return;
    await this.presentTeamMembersDetailsModal();
    params.delete('your_account');
    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  ngAfterViewInit() {
    const el = this.elementRef.nativeElement;
    const container: HTMLElement = el.querySelector('.container-fluid');

    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        // Measure natural nav width the first time (or whenever nav is visible)
        if (this.navLinksNaturalWidth === 0) {
          const ul: HTMLElement = el.querySelector('.navbar-nav');
          const w = ul?.scrollWidth ?? 0;
          if (w > 0) this.navLinksNaturalWidth = w;
        }
        if (this.navLinksNaturalWidth === 0) return;

        const brand: HTMLElement = el.querySelector('.app-brand');
        const actions: HTMLElement = el.querySelector('.navbar-actions');
        const needed = (brand?.offsetWidth ?? 0) + this.navLinksNaturalWidth + (actions?.offsetWidth ?? 0);
        this.forceMobile = needed > container.clientWidth;
      });
    });

    this.resizeObserver.observe(container);
  }

  ngOnDestroy() {
    clearInterval(this.refreshInterval);
    this.resizeObserver?.disconnect();
    this.todosChangedSub?.unsubscribe();
  }

  startRefreshTimer() {
    clearInterval(this.refreshInterval);
    this.refreshInterval = setInterval(() => {
      this.loadNotifications();
      this.loadMissingInfoAwaitingReviewCount();
      this.loadOpenTodoCount();
    }, 2 * 60 * 1000);
  }

  refreshNotifications() {
    this.loadNotifications();
    this.loadMissingInfoAwaitingReviewCount();
    this.loadOpenTodoCount();
    this.startRefreshTimer();
  }

  async loadNotifications() {
    this.loadingNotifications = true;
    try {
      const data: any = await this.apiService.get('notifications');
      if (data?.success) {
        this.notifications = data.data || [];
        if(data.data) data.data.forEach(element => { element.date = element.date + 'Z'; });
        this.unreadCount = this.notifications.filter(n => !n.marked_as_read).length;
      } else if (data?.status_code === 403) {
        // The session cookie stopped being accepted server-side (e.g. it timed out from
        // inactivity) - rather than leaving the nav bar silently broken, log the user out and
        // send them back to login with an explanation.
        this.authService.logout('timed_out');
      }
    } finally {
      this.loadingNotifications = false;
    }
  }

  // Fulfilled by the client (client_completed = 1) but not yet reviewed/closed out by a team
  // member (completed = 0) - these are the ones sitting in someone's queue unnoticed. Scoped to
  // the last two weeks so the badge doesn't keep counting old items nobody's ever going to review.
  async loadMissingInfoAwaitingReviewCount() {
    const start_date = dayjs().subtract(14, 'day').toISOString();
    const end_date = dayjs().add(1, 'day').toISOString();
    const data: any = await this.apiService.get(`missing_info?completed=0&client_completed=1&count=1&client_completed_start_date=${start_date}&client_completed_end_date=${end_date}`);
    if (data?.success && data.data && data.data[0]) this.missingInfoAwaitingReviewCount = data.data[0].count;
  }

  async loadOpenTodoCount() {
    this.openTodoCount = await this.todosService.GetMyOpenCount();
  }

  toggleNotifications(event: Event) {
    event.stopPropagation();
    this.showNotifications = !this.showNotifications;
    if (!this.showNotifications) this.selectedNotificationType = null;
  }

  selectNotificationType(type: NotificationTypes) {
    this.selectedNotificationType = type;
  }

  backToTypeList() {
    this.selectedNotificationType = null;
  }

  getNotificationsByType(type: NotificationTypes) {
    return this.notifications.filter(n => n.type === type);
  }

  // Notification types with the most unread first; types with the same number of unread keep their usual order
  get sortedNotificationTypeConfig() {
    return this.notificationTypeConfig
      .map((cfg, index) => ({ cfg, index, unread: this.getUnreadCountByType(cfg.type) }))
      .sort((a, b) => b.unread - a.unread || a.index - b.index)
      .map(item => item.cfg);
  }

  getUnreadCountByType(type: NotificationTypes) {
    return this.notifications.filter(n => n.type === type && !n.marked_as_read).length;
  }

  // notification-description is clamped to 2 lines via CSS (-webkit-line-clamp) - that clips
  // silently with no strong visual cue, so easy to miss that there's more text than what's shown.
  // Rather than measuring the rendered element's scrollHeight (fragile inside an *ngFor that
  // re-renders on every notification poll), this is a plain character-count heuristic tuned to
  // roughly 2 lines at the panel's fixed width/font-size - slightly generous on purpose, since a
  // false "Show more" on a borderline-short description is harmless but a missed one isn't.
  private static readonly DESCRIPTION_TRUNCATE_THRESHOLD = 90;
  IsDescriptionTruncated(description: string): boolean {
    if (!description) return false;
    // Each "\n" renders as its own line (white-space: pre-line), so more than 2 lines clips too.
    return description.length > TopNavbarComponent.DESCRIPTION_TRUNCATE_THRESHOLD
      || description.trim().split('\n').length > 2;
  }

  // The hover tooltip is rendered as HTML (data-bs-html), so "\n" has to become <br> there.
  GetDescriptionTooltip(description: string): string {
    return (description ?? '').trim().replace(/\r?\n/g, '<br>');
  }

  // "Show more" sits inside .notification-item, whose own (click) marks the notification as read -
  // without stopping propagation here, a click just bubbled up and did that instead of expanding
  // anything. Expansion state is tracked on the notification object itself rather than by guid in a
  // component field, since that's simplest given *ngFor already iterates these same objects.
  toggleDescriptionExpanded(notification: any, event: Event) {
    event.stopPropagation();
    notification._expanded = !notification._expanded;
  }

  getViewButtonLabel(type: NotificationTypes): string {
    if (type === NotificationTypes.task) return 'View Task';
    if (type === NotificationTypes.email) return 'View Email';
    if (type === NotificationTypes.project) return 'View Project';
    if (type === NotificationTypes.appointment) return 'View Appointment';
    if (type === NotificationTypes.client) return 'View Client';
    if (type === NotificationTypes.company) return 'View Company';
    if (type === NotificationTypes.step) return 'View Action';
    if (type === NotificationTypes.files) return 'View Folder';
    if (type === NotificationTypes.todo) return 'View To Do';
    if (type === NotificationTypes.missing_info) return 'View Missing Info';
    return 'View';
  }

  get selectedNotificationTypeLabel(): string {
    return this.notificationTypeConfig.find(c => c.type === this.selectedNotificationType)?.label ?? '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showNotifications = false;
    }
  }

  async markAsRead(notification: any) {
    if (!notification.marked_as_read) {
      notification.marked_as_read = true;
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      await this.apiService.put(`notifications/${notification.guid}/read`, {});
    }
  }

  async viewNotificationDetail(notification: any, open_comment = false) {
    console.log("Viewing notification detail:", notification);
    await this.markAsRead(notification);
    // "View Comment" on an @mention opens the comments tab of what it was left on, at that comment; the normal view button just
    // opens the item
    const mention = open_comment ? this.GetMentionProps(notification) : { is_mention: false, props: (tab: string) => ({}) };
    if (notification.type === NotificationTypes.task) {
      this.presentDetailsPage(notification.linked_guid, TaskDetailsPage, mention.props('Comments'));
    } else if (notification.type === NotificationTypes.email) {
      this.presentDetailsPage(notification.linked_guid, EmailDetailsPage, mention.props('comments'));
    } else if (notification.type === NotificationTypes.project) {
      this.presentDetailsPage(notification.linked_guid, ProjectDetailsPage, mention.props('Comments'));
    } else if (notification.type === NotificationTypes.appointment) {
      this.viewAppointmentNotification(notification.linked_guid);
    } else if (notification.type === NotificationTypes.step) {
      this.presentDetailsPage(notification.linked_guid, StepDetailsPage, mention.props('Comments'));
    } else if (notification.type === NotificationTypes.client) {
      this.presentClientDetailsPage(notification.linked_guid, mention.props('comments'));
    } else if (notification.type === NotificationTypes.company) {
      this.presentCompanyDetailsPage(notification.linked_guid, mention.props('comments'));
    } else if (notification.type === NotificationTypes.files) {
      // A plain Router navigation (not window.location.href) - explorer.component.ts listens to
      // ActivatedRoute.fragment so it picks this up even if the file explorer page is already open.
      this.router.navigate(['/file-explorer'], { fragment: notification.linked_guid });
    } else if (notification.type === NotificationTypes.todo) {
      this.presentTodoDetails(notification.linked_guid);
    } else if (notification.type === NotificationTypes.missing_info) {
      // Linked to the missing info item's guid - older notifications only stored the page path
      // ("/missing-info"), so those just open the Missing Info page.
      const linked = String(notification.linked_guid || '');
      if (linked && !linked.startsWith('/')) this.presentDetailsPage(linked, MissingInfoDetailsPage);
      else this.router.navigate(['/missing-info']);
    }
  }

  // Whether a notification is someone @mentioning the signed-in team member in a comment (the notification only carries text, so it is
  // recognised by it), and the properties that open the comments tab at that comment
  private GetMentionProps(notification: any): { is_mention: boolean; props: (tab: string) => any } {
    const is_mention = /mentioned you/i.test(`${notification?.title || ''}`);
    const text = (/mentioned you:\s*([\s\S]*)$/i.exec(`${notification?.description || ''}`)?.[1] || '').trim();
    const user_guid = this.globalService.GetCurrentUser()?.guid;
    return {
      is_mention,
      props: (tab: string) => is_mention && user_guid ? { activeIndex: tab, focus_comment: { user_guid, text } } : {}
    };
  }

  IsMentionNotification(notification: any): boolean {
    return /mentioned you/i.test(`${notification?.title || ''}`);
  }

  async viewNotificationProject(notification: any) {
    await this.markAsRead(notification);
    this.presentDetailsPage(notification.project_guid, ProjectDetailsPage);
  }

  async markAllAsRead(type: NotificationTypes | null = null) {
    if (type === null) {
      this.notifications.forEach(n => n.marked_as_read = true);
    } else {
      this.notifications.filter(n => n.type === type).forEach(n => n.marked_as_read = true);
    }
    this.unreadCount = this.notifications.filter(n => !n.marked_as_read).length;
    await this.apiService.put('notifications/read-all', { type: type ?? -1 });
  }

  async logoutConfirm() {
    const alert = await this.alertController.create({
      backdropDismiss: false,
      header: 'Confirm Logout',
      message: 'Are you sure you want to logout?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Logout',
          handler: () => {
            // Delete session cookies
            this.authService.logout();
          }
        }
      ]
    });
    await alert.present();
  }

  async openAddressBookModal() {
    console.log("Open Address Book");
    const modal = await this.modalController.create({
      component: AddressBookComponent,
      cssClass: 'large-modal',
      componentProps: {

      },
      backdropDismiss: false

    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        console.log("Dismissedd");

      }
    });
    return await modal.present()
  }

  async openProjectTemplateBook() {
    console.log("Open Address Book");
    const modal = await this.modalController.create({
      component: TemplateBookComponent,
      cssClass: 'large-modal',
      componentProps: {

      },
      backdropDismiss: false

    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        console.log("Dismissedd");

      }
    });
    return await modal.present()
  }


  async ViewYourAccount() {

  }

  async presentTeamMembersDetailsModal() {
    let user = await this.globalService.GetCurrentUser();
    const modal = await this.modalController.create({
      component: DetailsComponent,
      cssClass: 'large-modal',
      componentProps: {
        team_member: user,
      },
    })
    modal.onDidDismiss().then((data: any) => {
    });

    return await modal.present()
  }

  // Opens the To Do's details (the same form as the Details button in the To Do list)
  async presentTodoDetails(guid: string) {
    const data: any = await this.todosService.GetTodo(guid);
    const todo = data?.success ? data.data?.[0] : null;
    if (!todo) {
      this.globalService.presentToastMessage(data?.message || 'This To Do could not be found. It may have been deleted.', false);
      return;
    }
    const modal = await this.modalController.create({
      component: TodoEditComponent,
      cssClass: 'large-modal',
      componentProps: { todo },
    });
    return await modal.present();
  }

  async presentDetailsPage(guid: string, component: any, extra_props: any = {}) {
    const modal = await this.modalController.create({
      component: component,
      cssClass: 'large-modal',
      componentProps: { guid: guid, ...extra_props },
      backdropDismiss: false
    });
    modal.onDidDismiss().then(() => {

    });
    return await modal.present();
  }

  // "appointment" notifications carry either an email guid (the old appointment-request flow -
  // the appointment always lived inside an email thread) or, for a client's standalone booking
  // (not tied to any email), the calendar_event's own guid prefixed "APT~" so the two are never
  // ambiguous. See ClientAppointments.cs's CreateNotification calls for where that prefix is set.
  async viewAppointmentNotification(linked_guid: string) {
    if (!linked_guid?.startsWith('APT~')) {
      this.presentDetailsPage(linked_guid, EmailDetailsPage);
      return;
    }
    const event_guid = linked_guid.substring(4);
    const data: any = await this.apiService.get(`calendar/events/${event_guid}`);
    const event = data?.success ? data.data?.[0] : null;
    if (!event) {
      this.globalService.presentToastMessage(data?.message || 'This appointment could not be found - it may have been cancelled.', false);
      return;
    }
    const team_members = (event.users || []).filter((u: any) => u.type == 1);
    const modal = await this.modalController.create({
      component: AppointmentCreateComponent,
      cssClass: 'tall-modal secondary-modal',
      backdropDismiss: false,
      componentProps: {
        view_only: true,
        event_guid: event.guid,
        force_type: event.type,
        title: event.title,
        description: event.description,
        start_time: event.start_time,
        in_end_time: event.end_time,
        in_users: team_members,
        in_selected_users: team_members.map((u: any) => u.guid),
        in_assigned_clients: this.globalService.ResolveAssignedClients(event)
      }
    });
    return await modal.present();
  }

  async presentClientDetailsPage(guid: string, extra_props: any = {}) {
    const modal = await this.modalController.create({
      component: ClientDetailsPage,
      cssClass: 'large-modal',
      componentProps: { client: { guid }, ...extra_props },
      backdropDismiss: false
    });
    return await modal.present();
  }

  async presentCompanyDetailsPage(guid: string, extra_props: any = {}) {
    const modal = await this.modalController.create({
      component: CompanyDetailsPage,
      cssClass: 'large-modal',
      componentProps: { company: { guid }, ...extra_props },
      backdropDismiss: false
    });
    return await modal.present();
  }


}
